import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getProductLine } from 'services/cataloge';
import { IProductLine } from 'types/category';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './PositionDropdown.scss';

interface IPositionDropdown {
  value: string;
  onChange: (id: number, name: string) => void;
}

/**
 * Selector de cargo (posición) con filtro local para la creación de herramienta desde cotización.
 * Carga las posiciones del catálogo al montar y renderiza el listado en un portal.
 */
const PositionDropdown = ({ value, onChange }: IPositionDropdown) => {
  const [searchString, setSearchString] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [positions, setPositions] = useState<IProductLine[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);

  const isFilled = !!value;
  const isDisabled = isLoading;

  const filteredPositions = positions.filter(position =>
    position.name?.toLowerCase().includes(searchString.toLowerCase())
  );

  /** Mantiene valueRef sincronizado para que handleClickOutside siempre lea el valor actual. */
  useEffect(() => {
    valueRef.current = value;
    setSearchString(value);
  }, [value]);

  /**
   * Carga las posiciones disponibles del catálogo al montar el componente.
   * Usa el flag cancelled para no actualizar estado ni notificar si el componente se desmonta antes de resolver.
   */
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getProductLine()
      .then(data => {
        if (!cancelled) setPositions(data.filter(position => position.id !== null && position.name !== null));
      })
      .catch(() => {
        if (!cancelled) displayErrorNotification();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Cierra el dropdown al hacer click fuera. Se crea una sola vez (deps: []).
   * Lee valueRef.current en lugar de capturar value del closure para evitar stale state.
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isInsideContainer = containerRef.current?.contains(e.target as Node);
      const isInsideDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchString(valueRef.current);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Mientras el dropdown está abierto, recalcula su posición al hacer scroll (en cualquier ancestro) o resize,
   * para que el portal flotante siga al input.
   */
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen]);

  /**
   * Calcula la posición del dropdown midiendo el espacio disponible arriba y abajo del input.
   * Si hay más espacio arriba (o no cabe mínimamente abajo), abre hacia arriba (flip).
   */
  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const maxHeight = 200;
    const minUsable = 120;

    if (spaceBelow >= minUsable || spaceBelow >= spaceAbove) {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(maxHeight, spaceBelow),
        zIndex: 9999
      });
    } else {
      const availableHeight = Math.min(maxHeight, spaceAbove);
      setDropdownStyle({
        position: 'fixed',
        top: rect.top - availableHeight - 4,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 9999
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (isDisabled) return;
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleSelect = (position: IProductLine) => {
    setSearchString(position.name);
    setIsOpen(false);
    onChange(position.id!, position.name);
  };

  /** Cierra el dropdown con la tecla Escape, restaura el texto al valor confirmado y quita el foco del input. */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape') return;
    setIsOpen(false);
    setSearchString(valueRef.current);
    inputRef.current?.blur();
  };

  return (
    <div className={`positionDropdown${isFilled ? ' filled' : ''}`} ref={containerRef}>
      <label className="fieldLabel">Cargos*</label>
      <div className="inputWrapper">
        <input
          ref={inputRef}
          value={searchString}
          placeholder={isLoading ? 'Cargando...' : 'Ej. Alta'}
          disabled={isDisabled}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>
      {isOpen && !isDisabled && createPortal(
        <div className="positionDropdownList" style={dropdownStyle} ref={dropdownRef}>
          {filteredPositions.length === 0 && (
            <div className="dropdownItem empty">Sin resultados</div>
          )}
          {filteredPositions.map(position => (
            <div
              key={position.id!}
              className="dropdownItem"
              onMouseDown={() => handleSelect(position)}
            >
              {position.name}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default PositionDropdown;
